/**
 * Standardized Error Handling for Peace EQ Application
 *
 * This module implements consistent error handling patterns across the codebase.
 */

import { Logger } from './logger';

const logger = Logger.createComponentLogger('error-handling');

/**
 * Standard error types for the Peace EQ application
 */
export enum ErrorType {
    SERVICE_ERROR = 'SERVICE_ERROR',
    VALIDATION_ERROR = 'VALIDATION_ERROR',
    AUDIO_ERROR = 'AUDIO_ERROR',
    IO_ERROR = 'IO_ERROR',
    CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
    PROCESSING_ERROR = 'PROCESSING_ERROR'
}

/**
 * Base class for all Peace EQ errors with consistent structure
 */
export class PeaceEQError extends Error {
    public readonly type: ErrorType;
    public readonly component: string;
    public readonly operation: string;
    public readonly cause?: Error;
    public readonly timestamp: Date;

    constructor(
        type: ErrorType,
        component: string,
        operation: string,
        message: string,
        cause?: Error
    ) {
        super(`[${component}] ${operation} failed: ${message}`);
        this.name = 'PeaceEQError';
        this.type = type;
        this.component = component;
        this.operation = operation;
        this.cause = cause;
        this.timestamp = new Date();

        // Maintain proper stack trace
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, PeaceEQError);
        }
    }

    /**
     * Get a detailed error description for logging
     */
    getDetailedMessage(): string {
        let details = `${this.type}: ${this.message}`;
        if (this.cause) {
            details += ` (caused by: ${this.cause.message})`;
        }
        details += ` at ${this.timestamp.toISOString()}`;
        return details;
    }

    /**
     * Convert to JSON for structured logging
     */
    toJSON(): object {
        return {
            type: this.type,
            component: this.component,
            operation: this.operation,
            message: this.message,
            timestamp: this.timestamp.toISOString(),
            cause: this.cause?.message
        };
    }
}

/**
 * Specific error classes for different types of failures
 */
export class ServiceError extends PeaceEQError {
    constructor(component: string, operation: string, message: string, cause?: Error) {
        super(ErrorType.SERVICE_ERROR, component, operation, message, cause);
        this.name = 'ServiceError';
    }
}

export class ValidationError extends PeaceEQError {
    constructor(component: string, operation: string, message: string, cause?: Error) {
        super(ErrorType.VALIDATION_ERROR, component, operation, message, cause);
        this.name = 'ValidationError';
    }
}

export class AudioError extends PeaceEQError {
    constructor(component: string, operation: string, message: string, cause?: Error) {
        super(ErrorType.AUDIO_ERROR, component, operation, message, cause);
        this.name = 'AudioError';
    }
}

export class IOError extends PeaceEQError {
    constructor(component: string, operation: string, message: string, cause?: Error) {
        super(ErrorType.IO_ERROR, component, operation, message, cause);
        this.name = 'IOError';
    }
}

export class ConfigurationError extends PeaceEQError {
    constructor(component: string, operation: string, message: string, cause?: Error) {
        super(ErrorType.CONFIGURATION_ERROR, component, operation, message, cause);
        this.name = 'ConfigurationError';
    }
}

export class ProcessingError extends PeaceEQError {
    constructor(component: string, operation: string, message: string, cause?: Error) {
        super(ErrorType.PROCESSING_ERROR, component, operation, message, cause);
        this.name = 'ProcessingError';
    }
}

/**
 * Result type for operations that may fail
 * Provides a type-safe alternative to throwing exceptions
 */
export type Result<T, E = PeaceEQError> = 
    | { success: true; data: T }
    | { success: false; error: E };

/**
 * Utility functions for creating Result types
 */
export const Result = {
    /**
     * Create a successful result
     */
    success<T>(data: T): Result<T> {
        return { success: true, data };
    },

    /**
     * Create a failed result
     */
    failure<T>(error: PeaceEQError): Result<T> {
        return { success: false, error };
    },

    /**
     * Wrap a potentially throwing function in a Result
     */
    try<T>(fn: () => T, component: string, operation: string): Result<T> {
        try {
            return Result.success(fn());
        } catch (error) {
            const peaceEQError = error instanceof PeaceEQError 
                ? error 
                : new ServiceError(component, operation, error instanceof Error ? error.message : String(error), error instanceof Error ? error : undefined);
            return Result.failure(peaceEQError);
        }
    },

    /**
     * Wrap an async function in a Result
     */
    async tryAsync<T>(fn: () => Promise<T>, component: string, operation: string): Promise<Result<T>> {
        try {
            const data = await fn();
            return Result.success(data);
        } catch (error) {
            const peaceEQError = error instanceof PeaceEQError 
                ? error 
                : new ServiceError(component, operation, error instanceof Error ? error.message : String(error), error instanceof Error ? error : undefined);
            return Result.failure(peaceEQError);
        }
    }
};

/**
 * Global error handler for unhandled errors
 */
export class ErrorHandler {
    private static instance?: ErrorHandler;

    private constructor() {
        this.setupGlobalHandlers();
    }

    /**
     * Get the singleton instance
     */
    public static getInstance(): ErrorHandler {
        if (!ErrorHandler.instance) {
            ErrorHandler.instance = new ErrorHandler();
        }
        return ErrorHandler.instance;
    }

    /**
     * Setup global error handlers for the browser environment
     */
    private setupGlobalHandlers(): void {
        if (typeof window !== 'undefined') {
            // Handle unhandled promise rejections
            window.addEventListener('unhandledrejection', (event) => {
                logger.error('Unhandled promise rejection:', event.reason);
                this.handleError(event.reason, 'global', 'unhandled-promise-rejection');
                // Don't prevent default to allow console logging
            });

            // Handle uncaught errors
            window.addEventListener('error', (event) => {
                logger.error('Uncaught error:', event.error);
                this.handleError(event.error, 'global', 'uncaught-error');
            });
        }
    }

    /**
     * Central error handling with consistent logging and notification
     */
    public handleError(error: unknown, component: string, operation: string): void {
        const peaceEQError = this.normalizeError(error, component, operation);
        
        // Log the error with full details
        logger.error(peaceEQError.getDetailedMessage(), peaceEQError.toJSON());

        // In a real application, you might want to:
        // 1. Send error to monitoring service (e.g., Sentry)
        // 2. Show user-friendly notification
        // 3. Trigger error recovery mechanisms
        
        // For now, we'll just ensure it's properly logged
        console.error('Peace EQ Error:', peaceEQError);
    }

    /**
     * Convert any error to a standardized PeaceEQError
     */
    private normalizeError(error: unknown, component: string, operation: string): PeaceEQError {
        if (error instanceof PeaceEQError) {
            return error;
        }

        if (error instanceof Error) {
            return new ServiceError(component, operation, error.message, error);
        }

        // Handle non-Error objects
        const message = typeof error === 'string' ? error : 'Unknown error occurred';
        return new ServiceError(component, operation, message);
    }

    /**
     * Handle errors with user notification
     */
    public handleErrorWithNotification(error: unknown, component: string, operation: string, userMessage?: string): void {
        this.handleError(error, component, operation);
        
        // Show user-friendly message if available
        if (userMessage && typeof window !== 'undefined') {
            // In a real application, this would use the notification system
            console.warn('User notification:', userMessage);
        }
    }
}

/**
 * Convenience function to get the error handler instance
 */
export function getErrorHandler(): ErrorHandler {
    return ErrorHandler.getInstance();
}

/**
 * Decorator for automatic error handling on class methods
 */
export function handleErrors(component: string) {
    return function<T extends { new (...args: any[]): object }>(constructor: T) {
        return class extends constructor {
            constructor(...args: any[]) {
                super(...args);
                
                // Wrap all methods with error handling
                const prototype = Object.getPrototypeOf(this);
                Object.getOwnPropertyNames(prototype).forEach(name => {
                    const descriptor = Object.getOwnPropertyDescriptor(prototype, name);
                    if (descriptor && typeof descriptor.value === 'function' && name !== 'constructor') {
                        const originalMethod = descriptor.value;
                        descriptor.value = function(...methodArgs: any[]) {
                            try {
                                return originalMethod.apply(this, methodArgs);
                            } catch (error) {
                                getErrorHandler().handleError(error, component, name);
                                throw error; // Re-throw for caller to handle
                            }
                        };
                        Object.defineProperty(prototype, name, descriptor);
                    }
                });
            }
        };
    };
}